// frontend/src/components/routineWizard/RoutineCreateChooser.js
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Sparkles, Plus, ChevronRight, AlertTriangle, Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../contexts/SubscriptionContext';

export default function RoutineCreateChooser({
  open,
  onOpenChange,
  mode = 'personal',
  personalRoutinesCount = 0,
  maxPersonalRoutines = 2,
  onPickScratch,
  onPickWizard,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isFreeUser } = useSubscription() || {};

  const atLimit = mode === 'personal' && (isFreeUser?.() ?? false) && personalRoutinesCount >= maxPersonalRoutines;

  const handlePick = (cb) => {
    if (atLimit) return;
    cb?.();
    onOpenChange?.(false);
  };

  const goUpgrade = () => {
    onOpenChange?.(false);
    navigate('/pricing');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden gap-0">
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle className="text-lg font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {t('routines.wizard.chooser.title')}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {t('routines.wizard.chooser.subtitle')}
          </DialogDescription>
        </DialogHeader>

        {atLimit && (
          <div className="mx-5 mb-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 flex items-start gap-2 animate-in fade-in">
            <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-amber-200 leading-relaxed">
                {t('routines.wizard.chooser.limitReached.banner', { max: maxPersonalRoutines })}
              </p>
              <Button
                size="sm"
                variant="ghost"
                onClick={goUpgrade}
                className="h-7 px-2 mt-1.5 text-xs text-amber-300 hover:text-amber-200 hover:bg-amber-500/20"
                data-testid="chooser-upgrade-cta"
              >
                <Crown className="h-3 w-3 mr-1" />
                {t('routines.wizard.chooser.limitReached.cta')}
              </Button>
            </div>
          </div>
        )}

        <div className="px-5 pb-5 space-y-3">
          {/* Trainer card */}
          <button
            type="button"
            disabled={atLimit}
            onClick={() => handlePick(onPickWizard)}
            className={`group w-full text-left rounded-xl border-2 border-primary/40 bg-gradient-to-br from-primary/10 to-orange-500/5 backdrop-blur-sm p-4 min-h-[120px] transition-all duration-200 animate-in fade-in slide-in-from-bottom-2 relative ${atLimit ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary/70 hover:shadow-[0_0_20px_-5px_rgba(255,165,0,0.4)]'}`}
            data-testid="chooser-pick-wizard"
          >
            <span className="absolute top-2 right-2 text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-primary/20 text-primary border border-primary/30">
              {t('routines.wizard.chooser.trainer.recommended')}
            </span>
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-primary/20 p-2.5 group-hover:bg-primary/30 transition-colors">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-base mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {t('routines.wizard.chooser.trainer.title')}
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {t('routines.wizard.chooser.trainer.subtitle')}
                </p>
              </div>
              {!atLimit && (
                <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all flex-shrink-0 self-center" />
              )}
            </div>
          </button>

          {/* Scratch card */}
          <button
            type="button"
            disabled={atLimit}
            onClick={() => handlePick(onPickScratch)}
            className={`group w-full text-left rounded-xl border border-border/50 bg-card/40 backdrop-blur-sm p-4 min-h-[120px] transition-all duration-200 animate-in fade-in slide-in-from-bottom-2 ${atLimit ? 'opacity-50 cursor-not-allowed' : 'hover:border-border hover:bg-card/60'}`}
            style={{ animationDelay: '80ms' }}
            data-testid="chooser-pick-scratch"
          >
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-muted p-2.5 group-hover:bg-muted/80 transition-colors">
                <Plus className="h-5 w-5 text-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-base mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {t('routines.wizard.chooser.scratch.title')}
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {t('routines.wizard.chooser.scratch.subtitle')}
                </p>
              </div>
              {!atLimit && (
                <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-foreground group-hover:translate-x-0.5 transition-all flex-shrink-0 self-center" />
              )}
            </div>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
